<template>
  <v-card flat>
    <v-card-title class="page-head">
      <div class="ph-left">
        <div class="ph-icon"><v-icon color="#1565c0">mdi-human-child</v-icon></div>
        <div>
          <div class="ph-title">All Registered Children</div>
          <div class="ph-sub">Printable list of all children</div>
        </div>
      </div>
      <v-spacer />
      <v-btn outlined small color="#2e7d32" class="mr-2 export-btn" @click="exportDialog = true">
        <v-icon left small>mdi-file-excel-outline</v-icon>
        Export
      </v-btn>
      <v-btn class="ph-btn" @click="printReport">
        <v-icon left small>mdi-printer</v-icon>
        Print
      </v-btn>
    </v-card-title>

    <v-card-text>
      <div class="report-meta no-print">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search..."
          dense
          outlined
          hide-details
          class="report-search"
        />
        <v-select
          v-model="selectedDistrict"
          :items="districts"
          item-text="district_name"
          item-value="district_name"
          label="District"
          prepend-inner-icon="mdi-map-marker-outline"
          dense
          outlined
          hide-details
          clearable
          class="report-search"
        />
        <v-spacer />
        <span class="report-count">
          <v-icon small color="#1565c0">mdi-format-list-numbered</v-icon>
          {{ filteredRows.length }} child{{ filteredRows.length === 1 ? '' : 'ren' }}
        </span>
      </div>

      <div id="all-children-print">
        <div class="print-header">
          <img :src="logoSrc" alt="Logo" class="print-logo" />
          <div class="print-head-text">
            <div class="print-org">Ministry of Women, Family Affairs &amp; Human Rights</div>
            <div class="print-report-title">All Registered Children Report</div>
          </div>
          <div class="print-date">Generated<br /><strong>{{ currentDate }}</strong></div>
        </div>

        <v-data-table
          class="elevation-0"
          :headers="headers"
          :items="filteredRows"
          :search="search"
          dense
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.index="{ index }">
            <span class="cell-muted">{{ index + 1 }}</span>
          </template>

          <template v-slot:item.childimage="{ item }">
            <v-avatar size="36" class="avatar-ring">
              <v-img v-if="item.childimage" :src="uploadBaseUrl + item.childimage" />
              <v-icon v-else small color="#90a4ae">mdi-human-child</v-icon>
            </v-avatar>
          </template>

          <template v-slot:item.childname="{ item }">
            <span class="cell-name">{{ item.childname }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="item.status === 'Active' ? '#e8f5e9' : '#ffebee'"
              :text-color="item.status === 'Active' ? '#2e7d32' : '#c62828'"
            >
              {{ item.status || '—' }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="44" color="#b0bec5">mdi-human-child</v-icon>
              <div class="empty-text">No children found</div>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>

    <ExportDialog v-model="exportDialog" :columns="exportColumns" @export="onExport" />
  </v-card>
</template>

<script>
import axios from 'axios';
import LogoImg from '@/assets/realmow.png';
import { exportCsv } from '@/utils/reportExport';
import ExportDialog from '@/components/ExportDialog.vue';

const API_URL = process.env.VUE_APP_API_URL;
const IMAGE_BASE = process.env.VUE_APP_IMAGE_BASE;

export default {
  name: 'AllChildrenReport',
  components: { ExportDialog },
  data() {
    return {
      search: '',
      selectedDistrict: '',
      districts: [],
      exportDialog: false,
      exportColumns: [
        { text: 'No.', value: 'index' },
        { text: 'ID', value: 'id' },
        { text: 'Child Name', value: 'childname' },
        { text: 'Date of Birth', value: 'dateofbirth' },
        { text: 'Age', value: 'age' },
        { text: 'Sex', value: 'sex' },
        { text: 'Where Found', value: 'wherethechildfound' },
        { text: 'Case Description', value: 'casedescription' },
        { text: 'Health Status', value: 'healthstatus' },
        { text: 'Found Date', value: 'founddate' },
        { text: 'District', value: 'district' },
        { text: 'Status', value: 'status' },
        { text: 'Reg Date', value: 'regdate' },
      ],
      rows: [],
      headers: [
        { text: 'No.', value: 'index' },
        { text: 'ID', value: 'id' },
        { text: 'Child Name', value: 'childname' },
        { text: 'Date of Birth', value: 'dateofbirth' },
        { text: 'Age', value: 'age' },
        { text: 'Image', value: 'childimage', sortable: false },
        { text: 'Sex', value: 'sex' },
        { text: 'Where Found', value: 'wherethechildfound' },
        { text: 'Case Description', value: 'casedescription' },
        { text: 'Health Status', value: 'healthstatus' },
        { text: 'Found Date', value: 'founddate' },
        { text: 'Status', value: 'status' },
        { text: 'Reg Date', value: 'regdate' },
      ],
      uploadBaseUrl: IMAGE_BASE,
      logoSrc: LogoImg,
      currentDate: new Date().toLocaleDateString(),
    };
  },
  computed: {
    filteredRows() {
      if (!this.selectedDistrict) return this.rows;
      return this.rows.filter((r) => r.district === this.selectedDistrict);
    },
  },
  mounted() {
    this.fetchChildren();
    this.fetchDistricts();
  },
  methods: {
    fetchChildren() {
      axios.post(API_URL, { opcion: 10, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' }).then((res) => {
        this.rows = Array.isArray(res.data) ? res.data : [];
      });
    },
    fetchDistricts() {
      axios.post(API_URL, { opcion: 40 }).then((res) => {
        this.districts = res.data || [];
      }).catch(() => { this.districts = []; });
    },
    onExport(cols) {
      exportCsv('all-children-report', cols, this.filteredRows);
    },
    printReport() {
      const prtHtml = document.getElementById('all-children-print')?.innerHTML;
      if (!prtHtml) {
        window.print();
        return;
      }

      let stylesHtml = '';
      const styleNodes = document.querySelectorAll('link[rel="stylesheet"], style');
      styleNodes.forEach((node) => {
        stylesHtml += node.outerHTML;
      });

      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
      WinPrint.document.write(`<!DOCTYPE html>
        <html>
          <head>
            ${stylesHtml}
          </head>
          <body>
            ${prtHtml}
          </body>
        </html>`);
      WinPrint.document.close();

      WinPrint.onload = () => {
        WinPrint.focus();
        WinPrint.print();
        WinPrint.close();
      };
    },
  },
};
</script>

<style scoped>
/* print-header / print-logo / no-print styles live in app.css */
</style>
